import { Injectable } from '@angular/core';

import { Observable, catchError, defer, firstValueFrom, from, map, shareReplay } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { HttpErrorHandler } from 'src/app/services/error-handler/http-error-handler.service';
import { environment } from 'src/environments/environment';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AnalyticsDashboardData, VoiceAIMetrics, Intent, ICountByWorkList } from './types';
import { VAConversationResult } from 'src/app/pages/portal/voice-ai/components/dashboard-table/dashboard-table.interface';

const ANALYTICS_ROUTES = {
  GET_ANALYTICS_DASHBOARD_DATA: 'get_analytics_dashboard_data',
  SET_CONVERSATION_AGENT: 'set_conversation_agent',
  GET_VOICE_AI_METRICS: 'get_voice_ai_metrics',
  GET_VOICE_AI_CONVERSATIONS: 'get_voice_ai_conversations',
  GET_VOICE_AI_COUNT_BY_WORK_LIST: 'get_voice_ai_count_by_work_list',
  GET_INTENTS: 'get_intents',
};

@Injectable({
  providedIn: 'root',
})
export class ApiGatewayAnalyticsService {
  private baseUrl: string;
  private userToken: string | null | undefined;

  /**
   * Observable that provides a list of intents with consolidated descriptions.
   *
   * When an intent has a `consolidate_intent` value (pointing to another intent's ID),
   * its `intent_description` will be replaced with the description from the parent intent.
   * All other intent fields (intent_id, intent_code, voiceai_enabled, etc.) remain unchanged.
   *
   * This observable:
   * - Is lazily evaluated (deferred until first subscription)
   * - Caches results using shareReplay(1) to avoid duplicate HTTP requests
   * - Preserves all original Intent fields except intent_description
   *
   * @example
   * // Original intents from API:
   * // [
   * //   { intent_id: 1, intent_code: 'sales', intent_description: 'Sales Intent', consolidate_intent: null, voiceai_enabled: true },
   * //   { intent_id: 2, intent_code: 'sales-new', intent_description: 'New Car Sales', consolidate_intent: 1, voiceai_enabled: true }
   * // ]
   * //
   * // Consolidated result:
   * // [
   * //   { intent_id: 1, intent_code: 'sales', intent_description: 'Sales Intent', consolidate_intent: null, voiceai_enabled: true },
   * //   { intent_id: 2, intent_code: 'sales-new', intent_description: 'Sales Intent', consolidate_intent: 1, voiceai_enabled: true }
   * // ]
   */
  public consolidatedIntents$: Observable<Intent[]>;

  constructor(private http: HttpClient, private httpErrorHandler: HttpErrorHandler, private afAuth: AngularFireAuth) {
    this.baseUrl = environment.apiGatewayAnalytics.url;
    this.afAuth.idToken.subscribe(token => {
      this.userToken = token;
    });

    this.consolidatedIntents$ = defer(() => from(this.getIntents())).pipe(
      map(intents =>
        intents.map(intent => ({
          ...intent,
          intent_description: intent.consolidate_intent
            ? intents.find(i => i.intent_id === intent.consolidate_intent)?.intent_description ?? intent.intent_description
            : intent.intent_description,
        })),
      ),
      shareReplay(1),
    );
  }

  async setConversationAgent(legacyConversationId: string, agentName: string): Promise<void> {
    return this.makeAnalyticsRequest([ANALYTICS_ROUTES.SET_CONVERSATION_AGENT], {
      legacyConversationId,
      agentName,
    });
  }

  async getAnalyticsDashboardData(
    botId: string,
    startDate: Date,
    endDate: Date,
    detail: string = 'T',
    excludeProvisional: boolean = false,
  ): Promise<AnalyticsDashboardData> {
    return await this.makeAnalyticsRequest([ANALYTICS_ROUTES.GET_ANALYTICS_DASHBOARD_DATA], {
      botId,
      startDate,
      endDate,
      detail,
      excludeProvisional,
    });
  }

  async getVoiceAIMetrics(botId: string, startDate: string, endDate: string): Promise<VoiceAIMetrics> {
    return this.makeAnalyticsRequest([ANALYTICS_ROUTES.GET_VOICE_AI_METRICS], {
      botId,
      startDate,
      endDate,
    });
  }

  async getIntents(): Promise<Intent[]> {
    return this.makeAnalyticsRequest([ANALYTICS_ROUTES.GET_INTENTS], {});
  }

  async getVoiceAIConversations(
    botId: string,
    startDate: string,
    endDate: string,
    worklistFilter: string,
    intent: string,
    assignedTo: string | null,
    search: string,
    pageNumber: number,
    pageSize: number,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
  ): Promise<VAConversationResult> {
    return this.makeAnalyticsRequest([ANALYTICS_ROUTES.GET_VOICE_AI_CONVERSATIONS], {
      botId,
      startDate,
      endDate,
      worklistFilter,
      intent,
      search,
      agentName: assignedTo,
      pageNumber,
      pageSize,
      ...(sortBy ? { sortBy } : {}),
      ...(sortOrder ? { sortOrder } : {}),
    });
  }

  async getVoiceAICountByWorkList(
    botId: string,
    startDate: string,
    endDate: string,
    intent: string,
    assignedTo: string | null,
    search: string,
  ): Promise<ICountByWorkList[]> {
    return this.makeAnalyticsRequest([ANALYTICS_ROUTES.GET_VOICE_AI_COUNT_BY_WORK_LIST], {
      botId,
      startDate,
      endDate,
      intent,
      search,
      agentName: assignedTo,
    });
  }

  private async makeAnalyticsRequest<T>(actions: string[], parameters: Record<string, any>): Promise<T> {
    const url = `${this.baseUrl}analytics`;

    /*
      For the case when you refresh a page that makes any analytics request on init,
    */
    if (!this.userToken) {
      this.userToken = await firstValueFrom(this.afAuth.idToken);
    }
    return firstValueFrom(
      this.http
        .post<T>(
          url,
          { parameters, actions },
          {
            headers: {
              authorization: `${this.userToken}`,
            },
          },
        )
        .pipe(catchError(error => this.httpErrorHandler.handleApiGateWayAnalyticsError(error))),
    );
  }
}
